import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageSquare, Users, Search, Check } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  management: "Management",
  staff: "Staff",
};

export const NewConversationModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("direct");
  const [directory, setDirectory] = useState([]);
  const [query, setQuery] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("direct"); setQuery(""); setGroupName(""); setSelected([]);
    axios.get(`${API}/users/directory`).then((r) => setDirectory(r.data)).catch(() => setDirectory([]));
  }, [open]);

  const filtered = directory.filter((u) => u.name.toLowerCase().includes(query.toLowerCase()));

  const startDirect = async (u) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/conversations/direct`, { user_id: u.id });
      onClose();
      navigate(`/chat/${res.data.id}`);
    } catch {
      toast.error("Impossibile aprire la conversazione");
    } finally { setBusy(false); }
  };

  const toggleMember = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const createGroup = async () => {
    if (!groupName.trim()) { toast.error("Inserisci un nome per il gruppo"); return; }
    if (selected.length === 0) { toast.error("Seleziona almeno un membro"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/channels`, { name: groupName.trim(), members: selected });
      onClose();
      navigate(`/chat/${res.data.id}`);
    } catch {
      toast.error("Impossibile creare il gruppo");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col" data-testid="new-conversation-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit">Nuova conversazione</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2">
          <button
            onClick={() => setTab("direct")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors ${tab === "direct" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}
            data-testid="tab-direct"
          >
            <MessageSquare className="w-4 h-4" /> Privato
          </button>
          <button
            onClick={() => setTab("group")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors ${tab === "group" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}
            data-testid="tab-group"
          >
            <Users className="w-4 h-4" /> Gruppo
          </button>
        </div>

        {tab === "group" && (
          <Input
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Nome del gruppo"
            className="mt-1"
            data-testid="group-name-input"
          />
        )}

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca utente..."
            className="pl-9"
            data-testid="user-search-input"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1 min-h-[180px]">
          {filtered.length === 0 && <p className="text-sm text-stone-400 text-center py-8">Nessun utente trovato</p>}
          {filtered.map((u) => {
            const isSel = selected.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => (tab === "direct" ? startDirect(u) : toggleMember(u.id))}
                disabled={busy}
                className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${isSel ? "bg-amber-50 border border-amber-200" : "hover:bg-stone-50 border border-transparent"}`}
                data-testid={`directory-user-${u.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center text-sm font-semibold text-stone-600 flex-shrink-0">
                  {u.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{u.name}</p>
                  <p className="text-xs text-stone-400">{ROLE_LABELS[u.role] || u.role}</p>
                </div>
                {tab === "group" && isSel && <Check className="w-4 h-4 text-amber-600" />}
              </button>
            );
          })}
        </div>

        {tab === "group" && (
          <Button onClick={createGroup} disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 rounded-xl" data-testid="create-group-button">
            Crea gruppo {selected.length > 0 && `(${selected.length})`}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationModal;
